/* eslint-disable react/prop-types */
import { useState, useEffect } from "react";
import get from "lodash/get";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router";
import { Link } from "react-router-dom";
import { Formik, Form, Field, useFormikContext } from "formik";
import {
  Text,
  Heading,
  Banner,
  Stack,
  Box,
  TextLink,
  Button,
  Checkbox,
  Input,
} from "@chaser/components";
import first from "lodash/first";
import { ShieldCheck, CircleCheck } from "lucide-react";
import CloseIcon from "../../../../assets/receivables/ico-close-red.svg";
import AccountService from "../../../../services/account-service";
import { setUser } from "../../duck/actions/login";
import style from "./LoginForm.module.scss";
import { useHistoryState } from "../../../../hooks/useGlobalState";
import usePush from "../../../../hooks/usePush";
import AuthenticationService from "../../../../services/authentication-service";
import { logError } from "../../../../util/logging";
import LoadingMessage from "../../../../components/LoadingMessage";
import { parseQueryString } from "../../../../util";
import GoogleButton from "../SSO/GoogleButton";
import MicrosoftButton from "../SSO/MicrosoftButton";
import IntuitButton from "../SSO/IntuitButton";
import XeroButton from "../SSO/XeroButton";
import TotpSetupForm from "../../../account/components/Totp/TotpSetupForm";
import Logo from "../../../../components/Logo";
import LabeledFieldWithErrorLabel from "../../../../components/LabeledFieldWithErrorLabel/index.jsx";
import useSSORestrictedDomains from "../../../../hooks/useSSORestrictedDomains";

const VERIFY_RESEND_COOLDOWN = 30;
const DEFAULT_POST_LOGIN_PATH = "/";

function hasOrganisations(user) {
  return Array.isArray(user?.organisations) && user.organisations.length > 0;
}

function isConnectPath(pathname) {
  return pathname === "/connect" || pathname.startsWith("/connect/");
}

function getPostLoginPath(user, from) {
  const pathname = from?.pathname;
  const search = from?.search || "";

  if (!pathname || pathname === "/login" || pathname === "/signup") {
    return DEFAULT_POST_LOGIN_PATH;
  }

  // Do not restore onboarding/connect routes for users who already belong to an organisation.
  if (isConnectPath(pathname) && hasOrganisations(user)) {
    return DEFAULT_POST_LOGIN_PATH;
  }

  return `${pathname}${search}`;
}

function redirectAfterLogin(history, user, from) {
  history.push(getPostLoginPath(user, from));
}

function EmailVerificationRequired({ email }) {
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [resent, setResent] = useState(false);

  const { execute: executeResend } = usePush(
    AccountService.resendVerifyEmailEmail,
    {
      successToast: "Verification link sent",
      errorToast: "Failed to send verification link",
    },
  );

  useEffect(() => {
    if (cooldownSeconds === 0) return;
    const id = setTimeout(
      () => setCooldownSeconds((c) => Math.max(0, c - 1)),
      1000,
    );
    return () => clearTimeout(id);
  }, [cooldownSeconds]);

  async function resend() {
    if (!email || cooldownSeconds > 0) return;
    const [err] = await executeResend(email);
    if (err) return;
    setResent(true);
    setCooldownSeconds(VERIFY_RESEND_COOLDOWN);
  }

  return (
    <FormWrapper>
      <div className={style.loginFormHead}>
        <Logo color="White" />
      </div>
      <Box py="large" px={["small", "large"]}>
        <Stack gap="medium" alignItems="center" textAlign="center">
          <CircleCheck
            className={style.verifyEmailIcon}
            size={52}
            strokeWidth={2.2}
          />
          <Heading as="h2" className={style.authFormTitle}>
            Please verify your email address
          </Heading>
          <Text>
            Please check your inbox at{" "}
            <span className={style.verifyEmailAddress}>{email}</span> to verify
            your email address.
          </Text>
          <Text className={style.footnoteText}>
            {cooldownSeconds === 0 ? (
              <TextLink as="button" type="button" onClick={resend}>
                Resend link
              </TextLink>
            ) : (
              <span>Resend link in {cooldownSeconds}s</span>
            )}
            {resent && " (sent)"}
          </Text>
        </Stack>
      </Box>
    </FormWrapper>
  );
}

function FormWrapper({ children, width = "430px" }) {
  return (
    <div style={{ width }} className={style.loginPageFormContainer}>
      {children}
    </div>
  );
}

function SSOOnlyBanner() {
  return (
    <Banner
      intent="info"
      showCloseButton={false}
      icon={<ShieldCheck color="#3f84d6" size={18} />}
    >
      <Text as="span" size="small">
        Your organisation requires sign-in via SSO. Please use one of the
        options above.
      </Text>
    </Banner>
  );
}

function LoginEmailPasswordFields({ loadingLogin }) {
  const { values } = useFormikContext();
  const { isDomainRestricted } = useSSORestrictedDomains();
  const restricted = isDomainRestricted(values.email);

  return (
    <>
      <Stack gap="xsmall" textAlign="left" width="100%">
        <LabeledFieldWithErrorLabel
          className={style.fieldBlock}
          as={Input}
          icon="email"
          name="email"
          label="Email"
          data-testid="email-input"
          data-trackingid="email-input"
          placeholder="Enter your email address"
          width="100%"
        />

        {!restricted && (
          <LabeledFieldWithErrorLabel
            className={style.fieldBlock}
            as={Input}
            icon="lock"
            name="password"
            type="password"
            label="Password"
            data-testid="password-input"
            data-trackingid="password-input"
            autocomplete="off"
            placeholder="Enter password"
            width="100%"
          />
        )}
      </Stack>

      {restricted ? (
        <SSOOnlyBanner />
      ) : (
        <div className={style.forgotPasswordRow}>
          <TextLink className={style.fieldHintLink} as={Link} to="/recover">
            Forgot password?
          </TextLink>
        </div>
      )}

      {!restricted && (
        <Button
          data-testid="submit-button"
          data-trackingid="submit-button"
          loading={loadingLogin}
          type="submit"
          style={{ width: "100%" }}
        >
          Login
        </Button>
      )}
    </>
  );
}

function PasswordForm({
  setView,
  setTempToken,
  setStoredUser,
  setUnverifiedEmail,
}) {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  let { error } = parseQueryString(location.search);
  if (error) error = decodeURIComponent(error);
  const { execute: executeLogin, loading: loadingLogin } = usePush(
    AuthenticationService.login,
  );
  const [{ loginError = error, from = { pathname: "/" } }, setState] =
    useHistoryState();
  const { isDomainRestricted } = useSSORestrictedDomains();

  function clearError() {
    setState({ loginError: null });
  }

  return (
    <FormWrapper>
      <Formik
        initialValues={{
          email: "",
          password: "",
        }}
        onSubmit={async (values, actions) => {
          const { email, password } = values;
          if (isDomainRestricted(email)) {
            actions.setSubmitting(false);
            return;
          }
          const [err, res] = await executeLogin(email, password);
          actions.setSubmitting(false);

          if (err) {
            const message = get(
              err,
              "0.message",
              err?.message || "Something went wrong. Try again later",
            );
            if (message.includes("Email not verified")) {
              setUnverifiedEmail(email);
              setView("verifyEmail");
              return;
            }
            if (!/(password|attempts)/.test(message))
              logError(message, { error: err }, 'error');
            setState({ loginError: message });
            return;
          }

          const { user, token, totp } = res;

          if (totp === "REQUIRED") {
            setTempToken(token);
            setView("totp");
            return;
          }

          if (!user.totp && !user.hasTOTP && first(user.emails).verified) {
            const params = new URLSearchParams();
            params.append("totpsetup", true);
            history.push({ search: params.toString() });

            setStoredUser({ ...user, token });
            setTempToken(token);
            setView("totpSetupInfo");
            return;
          }
          const authenticatedUser = { ...user, token };
          dispatch(setUser(authenticatedUser));
          redirectAfterLogin(history, authenticatedUser, from);
        }}
      >
        <Form>
          <div className={style.loginFormHead}>
            <Logo color="White" />
          </div>
          <Box py="large" px={["small", "large"]}>
            <Stack gap="large" textAlign="center" width="100%">
              <Stack
                alignItems="stretch"
                gap="small"
                className={style.authBodyStack}
              >
                {loginError && (
                  <div className={style.loginErrorMessage}>
                    <p
                      className={style.loginErrorText}
                      data-testid="login-form-error-msg"
                      data-trackingid="login-form-error-msg"
                    >
                      {loginError}
                    </p>
                    <button
                      type="button"
                      className={style.loginErrorIcon}
                      onClick={() => {
                        clearError();
                      }}
                    >
                      <img alt="" src={CloseIcon} />
                    </button>
                  </div>
                )}
                <Stack
                  justifyContent="center"
                  alignItems="center"
                  gap="xsmall"
                  width="100%"
                >
                  <GoogleButton />
                  <MicrosoftButton />
                  <IntuitButton />
                  <XeroButton />
                </Stack>
                <div className={style.authDivider}>
                  <span>or sign in with email</span>
                </div>
                <LoginEmailPasswordFields loadingLogin={loadingLogin} />
                <div className={style.formFooterDivider} />
                <Text className={style.footnoteText}>
                  Don&apos;t have an account?{" "}
                  <TextLink as={Link} to="/signup">
                    Sign up
                  </TextLink>
                </Text>
              </Stack>
            </Stack>
          </Box>
        </Form>
      </Formik>
    </FormWrapper>
  );
}

function TotpSetupInfo({ setView, user, tempToken }) {
  const needsTOTP = user.needsTOTP;
  const dispatch = useDispatch();
  const history = useHistory();
  const [{ from = { pathname: "/" } }] = useHistoryState();

  return (
    <FormWrapper width="660px">
      <Formik>
        <Form>
          <div className={style.loginFormHead}>
            <Logo />
          </div>
          <Box p="large">
            <Stack gap="medium" textAlign="center">
              <Heading as="h2">Add an extra layer of security</Heading>
              <hr />
              <Stack gap="medium" textAlign="left">
                <Stack gap="small">
                  <Text fontSize="medium" fontWeight="bold">
                    Chaser + Authentication mobile app
                  </Text>
                  <Text>
                    Use a mobile authentication app to act as an additional
                    level of security when signing in. The app generates a
                    one-time code on mobile device which you then must enter
                    when signing in to Chaser.{" "}
                  </Text>
                </Stack>
                <Text>This is required for all users.</Text>
              </Stack>
              <hr />
              <Stack gap="xsmall">
                <Button
                  data-testid="continue-setup"
                  data-trackingid="continue-setup"
                  onClick={() => {
                    setView("totpSetup");
                  }}
                >
                  Continue to setup
                </Button>
                {!needsTOTP && (
                  <Button
                    appearance="minimal"
                    intent="info"
                    color="Blue AA"
                    onClick={() => {
                      const authenticatedUser = { ...user, token: tempToken };
                      dispatch(setUser(authenticatedUser));
                      redirectAfterLogin(history, authenticatedUser, from);
                    }}
                  >
                    Skip for now
                  </Button>
                )}
              </Stack>
            </Stack>
          </Box>
        </Form>
      </Formik>
    </FormWrapper>
  );
}

function TotpForm({ setView, tempToken, view }) {
  const dispatch = useDispatch();
  const history = useHistory();
  const [totpError, setTotpError] = useState("");
  const { execute: executeTotp, loading: loadingTotp } = usePush(
    AuthenticationService.loginTotp,
    {
      errorToast: false,
    },
  );
  const { execute: executeSendEmail, loading: loadingSendEmail } = usePush(
    AuthenticationService.totpSendEmail,
  );

  const [{ from = { pathname: "/" } }] = useHistoryState();

  useEffect(() => {
    if (view === "totpEmail") {
      executeSendEmail(tempToken);
    }
  }, [executeSendEmail, view, tempToken]);

  return (
    <FormWrapper>
      <Formik
        initialValues={{
          _reinit: view,
          code: "",
          remember: false,
        }}
        enableReinitialize
        onSubmit={async (values, actions) => {
          const { code, remember } = values;
          setTotpError("");

          const [err, res] = await executeTotp(
            code.replace(/\s/g, ""),
            tempToken,
            view,
            remember,
          );
          actions.setSubmitting(false);

          if (err) {
            setTotpError("Invalid code");
            return;
          }

          const { user, token } = res;
          const authenticatedUser = { ...user, token };
          dispatch(setUser(authenticatedUser));
          redirectAfterLogin(history, authenticatedUser, from);
        }}
      >
        <Form>
          <div className={style.loginFormHead}>
            <Logo />
          </div>
          <Box p="large">
            {loadingSendEmail ? (
              <LoadingMessage />
            ) : (
              <Stack gap="medium" textAlign="center">
                <Stack gap="small">
                  <Heading as="h2">
                    {view === "totp"
                      ? "Enter authentication code"
                      : "Check your email"}
                  </Heading>
                  <Text>
                    {view === "totp"
                      ? "Enter the 6-digit code from your authentication app"
                      : "Enter the code we sent you, to verify your login"}
                  </Text>
                </Stack>
                <Stack textAlign="left">
                  {totpError && (
                    <div className={style.loginErrorMessage}>
                      <p className={style.loginErrorText}>{totpError}</p>
                      <button
                        type="button"
                        className={style.loginErrorIcon}
                        onClick={() => setTotpError("")}
                      >
                        <img src={CloseIcon} alt="Dismiss" width={12} />
                      </button>
                    </div>
                  )}
                  <Field
                    as={Input}
                    icon="lock"
                    name="code"
                    placeholder="Enter code"
                    inputMode="numeric"
                    width="100%"
                    autoFocus
                  />

                  {view === "totp" && (
                    <Field name="remember" type="checkbox" as={Checkbox}>
                      Remember me for 30 days
                    </Field>
                  )}

                  <Button
                    loading={loadingTotp}
                    type="submit"
                    style={{ width: "100%" }}
                  >
                    Confirm login
                  </Button>
                </Stack>
                <hr />
                <Text>
                  {view === "totp" ? (
                    <TextLink onClick={() => setView("totpEmail")}>
                      Can&rsquo;t access the authentication app?
                    </TextLink>
                  ) : (
                    <TextLink onClick={() => setView("totp")}>
                      Sign in with authenticator
                    </TextLink>
                  )}
                </Text>
                <Text>
                  <TextLink onClick={() => setView("password")}>
                    Sign in with a different account
                  </TextLink>
                </Text>
              </Stack>
            )}
          </Box>
        </Form>
      </Formik>
    </FormWrapper>
  );
}

function TotpSetupScreen({ tempToken, storedUser }) {
  const history = useHistory();
  const dispatch = useDispatch();
  const [{ from = { pathname: "/" } }] = useHistoryState();
  const [isLoading, setIsLoading] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const { execute } = usePush(AuthenticationService.skipTotpSetup);

  const { execute: executeValidate, loading: loadingValidate } = usePush(
    AuthenticationService.validateTotpSetup,
    {
      errorToast: "Invalid code",
    },
  );

  async function onSubmit(values, actions) {
    const [err] = await executeValidate(
      values.code.replace(/\s/g, ""),
      tempToken,
    );
    actions.setSubmitting(false);
    if (err) return;
    dispatch(setUser(storedUser));
    execute(tempToken);
    redirectAfterLogin(history, storedUser, from);
  }

  return (
    <FormWrapper width="660px">
      <div className={style.loginFormHead}>
        <Logo />
      </div>
      <Box p="large">
        <TotpSetupForm
          tempToken={tempToken}
          onSubmit={onSubmit}
          isLoading={setIsLoading}
          isComplete={setIsComplete}
          buttons={
            <Stack gap="xsmall">
              <Button
                data-testid="complete-setup"
                data-trackingid="complete-setup"
                disabled={isLoading || !isComplete}
                loading={loadingValidate}
                type="submit"
              >
                Complete set up
              </Button>
            </Stack>
          }
        />
      </Box>
    </FormWrapper>
  );
}

function LoginForm() {
  const [view, setView] = useState("password");
  const [tempToken, setTempToken] = useState(null);
  const [storedUser, setStoredUser] = useState(null);
  const [unverifiedEmail, setUnverifiedEmail] = useState(null);

  const views = {
    password: (
      <PasswordForm
        setView={setView}
        setTempToken={setTempToken}
        setStoredUser={setStoredUser}
        setUnverifiedEmail={setUnverifiedEmail}
      />
    ),
    verifyEmail: <EmailVerificationRequired email={unverifiedEmail} />,
    totp: <TotpForm setView={setView} tempToken={tempToken} view={view} />,
    totpEmail: <TotpForm setView={setView} tempToken={tempToken} view={view} />,
    totpSetupInfo: (
      <TotpSetupInfo
        setView={setView}
        user={storedUser}
        tempToken={tempToken}
      />
    ),
    totpSetup: (
      <TotpSetupScreen
        setView={setView}
        tempToken={tempToken}
        storedUser={storedUser}
      />
    ),
  };

  return views[view];
}

export default LoginForm;
